import ReactModal from 'react-modal';
import { useCallback, useEffect, useState } from 'react';

import cs from 'classnames';

import { cn } from 'lib/helpers/utility';
import { DirtyTracker, useDirtyTracker } from 'lib/context/DirtyTrackerContext';

import Footer from './components/Footer';
import Header from './components/Header';
import SubModal from './components/SubModal/SubModal';
import ModalSidebar from './ModalSidebar/ModalSidebar';
import { MODAL_BODY_SPACE, MODAL_WIDTH } from './lib/index';
import SubModalContext from './components/SubModal/SubModalContext';

import './lib/Modal.scss';

const SUB_MODAL_ANIMATION_MS = 300;

const BaseModal = ({
  isOpen = false,
  handleClose,
  title,
  customTitle,
  children,
  width = 'small',
  isCentered = false,
  subModal: controlledSubModal,
  slideFromRight = true,
  slideFromBottom = false,
  isLarge = false,
  disableHeader = false,
  headButton,
  headerClassName,
  headerCloseIcon = true,
  headerCloseIconClassName,
  customHeader,
  prevButton,
  footer,
  isFooter = true,
  footerActions,
  className,
  portalClassName,
  shouldCloseOnOverlayClick = true,
  overlayClassName,
  bodyClassName,
  footerClassName,
  customStyling,
  modalCenterAnimation = false,
  isSidebar = false,
  sidebarItems = [],
  onScroll,
  listClassName,
  contentClassName,
  activeSidebarKey,
  onActiveChange,
  isHidden = false,
  isDirty,
  ...rest
}) => {
  const dirtyTracker = useDirtyTracker();

  const [internalSubModal, setInternalSubModal] = useState(null);
  const subModal = controlledSubModal !== undefined ? controlledSubModal : internalSubModal;

  const [activeSubModal, setActiveSubModal] = useState(subModal || null);
  const [subModalVisible, setSubModalVisible] = useState(!!subModal);

  useEffect(() => {
    if (subModal) {
      setActiveSubModal(subModal);
      const t = setTimeout(() => setSubModalVisible(true), 20);
      return () => clearTimeout(t);
    } else {
      setSubModalVisible(false);
      const t = setTimeout(() => setActiveSubModal(null), SUB_MODAL_ANIMATION_MS);
      return () => clearTimeout(t);
    }
  }, [subModal]);

  const openSubModal = useCallback((config) => setInternalSubModal(config), []);
  const closeSubModal = useCallback(() => setInternalSubModal(null), []);

  const handleSubModalClose = () => {
    activeSubModal?.actions?.onClose?.();
    if (controlledSubModal === undefined) {
      closeSubModal();
    }
  };

  // If isDirty is provided and we have DirtyTracker context, protect the close action
  const protectedClose = () => {
    if (isDirty !== undefined && dirtyTracker?.handleProtectedAction) {
      dirtyTracker.handleProtectedAction(handleClose);
    } else {
      handleClose();
    }
  };
  const hiddenStyle = isHidden ? { opacity: 0, pointerEvents: 'none' } : {};

  return (
    <SubModalContext.Provider value={{ openSubModal, closeSubModal }}>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={subModalVisible ? handleSubModalClose : protectedClose}
        contentLabel={
          typeof title === 'string'
            ? title
            : typeof customTitle === 'string'
              ? customTitle
              : 'Modal'
        }
        closeTimeoutMS={300}
        className={cn(
          'Modal',
          MODAL_WIDTH(width, isCentered),
          !slideFromRight && 'm-6 max-h-[calc(100vh-48px)] sm:m-4 sm:max-h-[calc(100vh-32px)]',
          isLarge && MODAL_WIDTH('full'),
          className
        )}
        overlayClassName={cs(
          'ModalOverlay',
          !isCentered && slideFromRight && 'ModalRightOverlay',
          !isCentered && slideFromBottom && 'ModalBottomOverlay',
          modalCenterAnimation && 'ModalCenterAnimation',
          overlayClassName
        )}
        portalClassName={cs(portalClassName)}
        ariaHideApp={false}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick && !subModalVisible}
        style={{ content: { ...customStyling, ...hiddenStyle }, overlay: hiddenStyle }}
        {...rest}>
        {!disableHeader && (
          <Header
            title={title}
            customTitle={customTitle}
            headButton={headButton}
            headerClassName={headerClassName}
            headerCloseIcon={headerCloseIcon}
            customHeader={customHeader}
            prevButton={prevButton}
            handleClose={protectedClose}
            headerCloseIconClassName={headerCloseIconClassName}
          />
        )}
        <div
          onScroll={onScroll}
          className={cn(
            'Modal__body',
            MODAL_BODY_SPACE(width, isCentered, isSidebar),
            bodyClassName
          )}>
          {isSidebar ? (
            <ModalSidebar
              items={sidebarItems}
              className={bodyClassName}
              listClassName={listClassName}
              contentClassName={contentClassName}
              activeKey={activeSidebarKey}
              onActiveChange={onActiveChange}
            />
          ) : (
            children
          )}
        </div>
        {footer ? (
          <div className={cs('Modal__footer h-[56px]', footerClassName)}>{footer}</div>
        ) : (
          <Footer
            footerActions={footerActions}
            handleClose={protectedClose}
            className={footerClassName}
            isFooter={isFooter}
          />
        )}
        <SubModal
          subModalContent={activeSubModal?.content}
          subModalVisible={subModalVisible}
          subModalTitle={activeSubModal?.title}
          subModalClassName={activeSubModal?.className}
          subModalPanelClassName={activeSubModal?.panelClassName}
          subModalActions={activeSubModal?.actions}
          subModalFooter={activeSubModal?.footer}
          subModalHeaderActions={activeSubModal?.headerActions}
          onClose={handleSubModalClose}
        />
      </ReactModal>
    </SubModalContext.Provider>
  );
};

/**
 * @param {import('./lib/propTypes').ModalProps} props
 */

const Modal = ({ isDirty, confirmMessage, confirmKey, ...props }) => {
  if (isDirty !== undefined) {
    return (
      <DirtyTracker isDirty={isDirty} message={confirmMessage} confirmKey={confirmKey}>
        <BaseModal isDirty={isDirty} {...props} />
      </DirtyTracker>
    );
  }
  return <BaseModal {...props} />;
};

export default Modal;
